/**
 * Robôs simples, só com regras fixas, para testar as regras da partida (item 4.3) e animar prévias.
 * Não são os bots do jogo (item 5.x): não pensam em tática, só correm atrás da bola e chutam no aro.
 * Determinísticos: dependem só do estado do mundo.
 */
import { ABILITIES, BALL, PITCH, hoopCenters, type Side } from "@besenliga/shared";
import { DEFENDS } from "./balls.js";
import type { AthleteState, Vec3 } from "./flight.js";
import { BUTTON, NO_INPUT, quantizeInput, type InputCommand, type InputFrame } from "./input.js";
import type { WorldState } from "./world.js";

const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const len = (a: Vec3) => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);

/** Comando para voar até `target`; `turbo` quando ainda está longe. */
function toward(a: AthleteState, target: Vec3, buttons = 0, turboFrom = 25): InputCommand {
  const d = sub(target, a.position);
  const h = Math.sqrt(d.x * d.x + d.z * d.z);
  // Chega devagar: perto do alvo (a bola, muitas vezes na mão de alguém) tira o pé, para não atropelar.
  const k = h > 0.5 ? Math.min(1, h / 10) / h : 0;
  const climb = Math.max(-1, Math.min(1, d.y / 4));
  const turbo = h > turboFrom && a.stamina > 0.5 ? BUTTON.turbo : 0;
  return quantizeInput(d.x * k, climb, d.z * k, buttons | turbo);
}

/** Uma vez a cada dois ticks: botão de ação "apertado" (o sim só reage à borda). */
const tap = (tick: number) => (tick % 2 === 0 ? BUTTON.action : 0);

export function robotInputs(w: WorldState, ids?: ReadonlySet<string>): InputFrame {
  const out: Record<string, InputCommand> = {};
  const balls = w.balls;
  if (!balls) return out;
  const pel = balls.pelota;
  const holder = pel.holder ? w.athletes.find((a) => a.id === pel.holder) : undefined;

  for (const a of w.athletes) {
    if (ids && !ids.has(a.id)) continue;
    const own = DEFENDS[a.team];
    const attack = -own as Side;
    const goal = hoopCenters(attack)[1] ?? { x: attack * PITCH.hoopX, y: 18, z: 0 };
    let cmd: InputCommand = NO_INPUT;

    // Companheiro pedindo a bola (botão do tick anterior): quem está com ela passa.
    const calling = w.athletes.some((m) => m.team === a.team && m.id !== a.id && m.buttons & BUTTON.call);
    if (holder?.id === a.id && calling) {
      cmd = toward(a, a.position, tap(w.tick));
    } else if (holder?.id === a.id) {
      if (a.role === "goleiro") {
        cmd = toward(a, a.position, tap(w.tick)); // passa logo
      } else {
        const dist = len(sub(goal, a.position));
        // Perto do aro: carrega o arremesso por ~0,7 s e solta.
        const charging = dist < 26 && a.chargeTicks < 20;
        cmd = toward(a, goal, charging ? BUTTON.charge : 0);
      }
    } else if (a.role === "goleiro") {
      const z = Math.max(-8, Math.min(8, pel.position.z));
      cmd = toward(a, { x: own * (PITCH.hoopX - 4), y: 16, z }, 0, 99);
    } else if (a.role === "batedor") {
      let near: Vec3 | null = null;
      let best = Infinity;
      for (const p of balls.petardos) {
        // Petardo recém-rebatido voa longe; correr atrás dele só serve para atropelar alguém.
        if (p.mode === "lancado") continue;
        const d = len(sub(p.position, a.position));
        if (d < best) {
          best = d;
          near = p.position;
        }
      }
      cmd = near
        ? toward(a, near, best < BALL.petardo.batReach ? tap(w.tick) : 0)
        : toward(a, { x: own * 20, y: 14, z: 0 });
    } else if (a.role === "cacador") {
      const fa = balls.faisca;
      const close = fa.active && len(sub(fa.position, a.position)) < BALL.faisca.catchReach + 0.5;
      cmd = fa.active
        ? toward(a, fa.position, close ? tap(w.tick) : 0, 6)
        : toward(a, { x: 0, y: 26, z: 0 }, 0, 99);
    } else if (holder && holder.team === a.team) {
      // Companheiro com a bola: abre à frente dele.
      const lane = a.id < holder.id ? -12 : 12;
      cmd = toward(a, { x: attack * 30, y: 14, z: lane });
    } else {
      cmd = toward(a, pel.position);
    }
    if (a.abilityCooldown === 0 && wantsAbility(w, a))
      cmd = { ...cmd, buttons: cmd.buttons | BUTTON.ability };
    out[a.id] = cmd;
  }
  return out;
}

/** Quando cada posição usa a habilidade, de um jeito simples. */
function wantsAbility(w: WorldState, a: AthleteState): boolean {
  const balls = w.balls;
  if (!balls) return false;
  const near = (p: Vec3, r: number) => len(sub(p, a.position)) < r;
  switch (a.role) {
    case "artilheiro":
      // Finta quando um Petardo caçando chega perto.
      return balls.petardos.some((p) => p.mode !== "descanso" && near(p.position, 5));
    case "goleiro":
      // Muralha com a Pelota solta chegando perto dos aros.
      return balls.pelota.holder === null && near(balls.pelota.position, 9);
    case "batedor":
      return balls.petardos.every((p) => near(p.position, ABILITIES.rebatidaDupla.reach));
    case "cacador":
      // Mergulho na Faísca quando ela está à frente e abaixo, a menos de 12 m.
      return (
        balls.faisca.active &&
        near(balls.faisca.position, 12) &&
        balls.faisca.position.y < a.position.y &&
        (balls.faisca.position.x - a.position.x) * a.heading.x +
          (balls.faisca.position.z - a.position.z) * a.heading.z >
          0
      );
  }
}
